/**
 * The application chrome: sidebar, topbar, and the outlet every screen renders into.
 *
 * Two things govern this file.
 *
 * **The markup is the prototype's.** `src/styles/app.css` is `assets/css/app.css`
 * copied verbatim, so the shell already has a stylesheet describing it —
 * `.app`, `.sidebar`, `.sidebar-scroll`, `.nav-group-label`, `.rail-toggle`,
 * `.sidebar-user`, `.main`, `.topbar`. An earlier version of this file invented
 * a parallel vocabulary (`.shell`, `.shell-main`, `.nav-list`, `.account-btn`)
 * and re-implemented the design in `react-overrides.css`. That is why the
 * sidebar used to sit *on top of* the page: `.sidebar` is `position: fixed`, and
 * only `.main` carries the `margin-left` that makes room for it. Using the real
 * class names is not cosmetic — it is what keeps the layout working.
 *
 * **The navigation comes from `/auth/me`, not from a hard-coded role table.**
 * The prototype lists which of its four demo roles may see each entry; this
 * asks the permission model instead. Grant someone `admin.audit:view` in the
 * admin console and the Audit Log entry appears on their next sign-in with no
 * front-end change.
 *
 * The dropdowns are React state rather than Bootstrap's JS. Only Bootstrap's
 * stylesheet is loaded (`main.jsx`), so `.show` is toggled here and the four
 * declarations Popper would otherwise compute live in `react-overrides.css`.
 */
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../hooks/useAuth.js';
import { visibleNav } from '../../routes/navModel.js';
import { toggleSidebar, toggleNavGroup, setSidebarOpen, dismissToast } from '../../features/ui/uiSlice.js';
import ToastStack from './ToastStack.jsx';
import { Avatar, TopProgressBar } from '../common/index.jsx';

/** Matches the `@media (min-width: 992px)` boundary the stylesheet uses. */
const DESKTOP = '(min-width: 992px)';
const isDesktop = () => window.matchMedia(DESKTOP).matches;

export default function AppShell() {
  const { user, organization, roles, signOut, canOpen } = useAuth();
  const { sidebarCollapsed, closedGroups, sidebarOpen, toasts } = useSelector((s) => s.ui);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const searchRef = useRef(null);

  const nav = useMemo(() => visibleNav(canOpen), [canOpen]);

  /* Stable identity: an inline arrow here is a new function on every render of
     the shell, which re-runs the lifetime effect inside every toast on screen. */
  const dismiss = useCallback((id) => dispatch(dismissToast(id)), [dispatch]);

  /**
   * The rail collapse is a desktop idea. Below 992px the same stylesheet turns
   * `.sidebar` into an off-canvas drawer, where "collapsed to icons" would mean
   * a 72px strip floating over the page.
   */
  const collapsed = sidebarCollapsed;

  const toggleNav = useCallback(() => {
    if (isDesktop()) dispatch(toggleSidebar());
    else dispatch(setSidebarOpen(!sidebarOpen));
  }, [dispatch, sidebarOpen]);

  /* ⌘K focuses search, ⌘B toggles the sidebar, Escape closes the drawer — the
     three shortcuts the prototype binds, and the ones the topbar advertises. */
  useEffect(() => {
    const onKey = (e) => {
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement?.tagName ?? '');
      const key = e.key.toLowerCase();
      if ((e.metaKey || e.ctrlKey) && key === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
      } else if ((e.metaKey || e.ctrlKey) && key === 'b' && !typing) {
        e.preventDefault();
        toggleNav();
      } else if (e.key === 'Escape') {
        dispatch(setSidebarOpen(false));
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [dispatch, toggleNav]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  return (
    <div className={`app ${collapsed ? 'nav-collapsed' : ''}`}>
      <Sidebar
        nav={nav}
        collapsed={collapsed}
        open={sidebarOpen}
        closedGroups={closedGroups}
        user={user}
        roles={roles}
        organization={organization}
        onToggleGroup={(g) => dispatch(toggleNavGroup(g))}
        onToggleRail={toggleNav}
        onNavigate={() => dispatch(setSidebarOpen(false))}
        onSignOut={handleSignOut}
      />

      {/* Tapping the dimmed page closes the drawer — the gesture people try first. */}
      <div
        className={`sidebar-backdrop ${sidebarOpen ? 'show' : ''}`}
        onClick={() => dispatch(setSidebarOpen(false))}
        aria-hidden="true"
      />

      <div className="main">
        <Topbar
          user={user}
          roles={roles}
          searchRef={searchRef}
          onToggleNav={toggleNav}
          onSignOut={handleSignOut}
        />
        <main className="page">
          <Outlet />
        </main>
      </div>

      <TopProgressBar />
      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Dropdown                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * A Bootstrap-styled menu driven by React state.
 *
 * Bootstrap's own dropdown needs its JavaScript bundle, which this app does not
 * load — and an imperative widget reaching into the DOM is the wrong shape for a
 * React tree anyway. `renderToggle` gets the props the trigger needs so the
 * caller keeps control of what the trigger looks like.
 */
function Dropdown({ className = '', menuClassName = '', renderToggle, children }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  return (
    <div className={className} ref={ref}>
      {renderToggle({
        onClick: () => setOpen((o) => !o),
        'aria-expanded': open,
        'aria-haspopup': 'menu',
      })}
      <ul
        className={`dropdown-menu ${menuClassName} ${open ? 'show' : ''}`}
        role="menu"
        onClick={() => setOpen(false)}
      >
        {children}
      </ul>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Sidebar                                                                     */
/* -------------------------------------------------------------------------- */

function Sidebar({
  nav, collapsed, open, closedGroups, user, roles, organization,
  onToggleGroup, onToggleRail, onNavigate, onSignOut,
}) {
  const primaryRole = roles?.find((r) => r.is_primary) ?? roles?.[0];

  /* In rail mode the submenu becomes a flyout. `.sidebar-scroll` and the
     group accordion wrapper both clip horizontal overflow (needed for their
     own vertical accordion animations), so an `absolute`-positioned flyout
     gets silently clipped/unclickable. `position: fixed`, placed from the
     trigger's own bounding rect on click, escapes that clipping entirely. */
  const [flyoutPos, setFlyoutPos] = useState({});

  return (
    <aside className={`sidebar ${collapsed ? 'collapsed' : ''} ${open ? 'open' : ''}`} id="appSidebar">
      <div className="sidebar-brand">
        <div className="brand-mark"><img src="/logo-icon.png" alt="EnGage" /></div>
        <div className="brand-text">
          <div className="brand-name"><img src="/logo-full.png" alt="EnGage — Sales | Marketing | Growth" className="brand-logo-hero-sidebar"/></div>
          {/* <div className="brand-sub">{organization?.name ?? 'Sales Cloud'}</div> */}
        </div>
      </div>

      <nav className="sidebar-scroll" aria-label="Primary">
        {nav.map((group) => {
          const isClosed = closedGroups.includes(group.group);
          return (
            <div key={group.group} className={`nav-group ${isClosed ? 'is-closed' : ''}`} data-group={group.group}>
              <button
                type="button"
                className="nav-group-label"
                aria-expanded={!isClosed}
                onClick={() => onToggleGroup(group.group)}
              >
                <span>{group.group}</span>
                <i className="bi bi-chevron-down gchev" aria-hidden="true" />
              </button>

              {/* Always rendered, never conditionally removed: the accordion is a
                  CSS grid-row transition, and in rail mode the stylesheet
                  re-opens closed groups so their icons stay reachable. */}
              <div className="nav-group-items">
                <div>
                  {group.items.map((item) => {
                    if (!item.children) {
                      return (
                        <NavLink
                          key={item.id}
                          to={item.to}
                          end={item.to === '/'}
                          className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                          onClick={onNavigate}
                          title={collapsed ? item.label : undefined}
                          data-rail-label={item.label}
                          {...(item.badge != null
                            ? { 'data-badge': item.badge, 'data-badge-tone': item.badgeTone ?? 'muted' }
                            : {})}
                        >
                          <i className={`bi ${item.icon}`} aria-hidden="true" />
                          <span className="nav-text">{item.label}</span>
                          {item.badge != null && (
                            <span className={`nav-badge ${item.badgeTone === 'danger' ? '' : 'muted'}`}>
                              {item.badge}
                            </span>
                          )}
                        </NavLink>
                      );
                    }
                    /* The one nested nav item — Reports' children. The whole row is a
                       single toggle (no navigation of its own, no active-highlight):
                       clicking it opens the submenu, in rail mode as a flyout since
                       there's no room to expand inline. Same open/closed mechanics as
                       a top-level group (`closedGroups`/`onToggleGroup`), namespaced
                       with `item:` so its key can't collide with a group name. */
                    const subKey = `item:${item.id}`;
                    const isSubClosed = closedGroups.includes(subKey);
                    const pos = flyoutPos[item.id];
                    return (
                      <div key={item.id} className={`nav-item-parent ${isSubClosed ? 'is-closed' : ''}`}>
                        <button
                          type="button"
                          className="nav-item nav-item-trigger"
                          aria-expanded={!isSubClosed}
                          title={collapsed ? item.label : undefined}
                          onClick={(e) => {
                            if (collapsed) {
                              const rect = e.currentTarget.getBoundingClientRect();
                              setFlyoutPos((p) => ({ ...p, [item.id]: { top: rect.top, left: rect.right + 10 } }));
                            }
                            onToggleGroup(subKey);
                          }}
                        >
                          <i className={`bi ${item.icon}`} aria-hidden="true" />
                          <span className="nav-text">{item.label}</span>
                          <i className="bi bi-chevron-down nav-item-chev" aria-hidden="true" />
                        </button>
                        <div
                          className="nav-subitems"
                          style={collapsed && pos ? { top: pos.top, left: pos.left } : undefined}
                        >
                          <div>
                            {item.children.map((child) => (
                              <NavLink
                                key={child.id}
                                to={child.to}
                                end
                                className="nav-subitem"
                                onClick={onNavigate}
                              >
                                <span className="nav-text">{child.label}</span>
                              </NavLink>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}

        {nav.length === 0 && (
          <p className="t-xs px-3 py-4" style={{ color: 'var(--on-dark-2)' }}>
            No screens are granted to your roles. An administrator needs to assign you a role
            before this application has anything to show you.
          </p>
        )}
      </nav>

      <div className="sidebar-foot">
        <button
          type="button"
          className="rail-toggle"
          onClick={onToggleRail}
          aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          title={collapsed ? 'Expand menu' : 'Collapse menu'}
        >
          <i className="bi bi-chevron-double-left" aria-hidden="true" />
          <span className="rail-text">Collapse menu</span>
        </button>

        <Dropdown
          className="dropup"
          menuClassName="w-100"
          renderToggle={(props) => (
            <button type="button" className="sidebar-user w-100 text-start border-0" {...props}>
              <Avatar name={user?.full_name ?? '?'} size={27} />
              <div className="flex-grow-1 min-w-0 user-text">
                <div className="name text-truncate">{user?.full_name}</div>
                <div className="role text-truncate">{primaryRole?.name ?? primaryRole?.code ?? '—'}</div>
              </div>
              <i className="bi bi-chevron-expand user-chev" style={{ fontSize: 12, opacity: 0.6 }} aria-hidden="true" />
            </button>
          )}
        >
          <li className="dropdown-header">Signed in as</li>
          <li><span className="dropdown-item-text t-xs ink-3 px-2 pb-2 d-block text-truncate">{user?.email}</span></li>
          <li><hr className="dropdown-divider" /></li>
          <li><NavLink className="dropdown-item" to="/profile"><i className="bi bi-person me-2" />My profile</NavLink></li>
          <li><NavLink className="dropdown-item" to="/profile/sessions"><i className="bi bi-laptop me-2" />Active sessions</NavLink></li>
          <li><NavLink className="dropdown-item" to="/profile/password"><i className="bi bi-key me-2" />Change password</NavLink></li>
          <li><hr className="dropdown-divider" /></li>
          <li>
            <button type="button" className="dropdown-item danger w-100 text-start border-0 bg-transparent" onClick={onSignOut}>
              <i className="bi bi-box-arrow-right me-2" />Sign out
            </button>
          </li>
        </Dropdown>
      </div>
    </aside>
  );
}

/* -------------------------------------------------------------------------- */
/* Topbar                                                                      */
/* -------------------------------------------------------------------------- */

function Topbar({ user, roles, searchRef, onToggleNav, onSignOut }) {
  const primaryRole = roles?.find((r) => r.is_primary) ?? roles?.[0];

  return (
    <header className="topbar">
      <button type="button" className="icon-btn" onClick={onToggleNav} title="Toggle navigation (⌘B)" aria-label="Toggle navigation">
        <i className="bi bi-list" aria-hidden="true" />
      </button>

      <div className="searchbox">
        <i className="bi bi-search" aria-hidden="true" />
        <input ref={searchRef} type="search" placeholder="Search leads, accounts, deals, quotes…" aria-label="Global search" />
        <kbd>⌘K</kbd>
      </div>

      <div className="ms-auto d-flex align-items-center gap-1">
        {primaryRole && (
          <span className="pill pill-brand d-none d-xl-inline-flex me-2">
            <i className="bi bi-shield-fill-check" aria-hidden="true" />
            {primaryRole.name ?? primaryRole.code}
          </span>
        )}

        <Dropdown
          className="dropdown d-none d-sm-block"
          menuClassName="dropdown-menu-end"
          renderToggle={(props) => (
            <button type="button" className="btn btn-add btn-sm-x d-flex align-items-center gap-1" {...props}>
              <i className="bi bi-plus-lg" aria-hidden="true" />
              <span className="d-none d-md-inline">Create</span>
            </button>
          )}
        >
          <li className="dropdown-header">Quick create</li>
          <li><NavLink className="dropdown-item" to="/leads"><i className="bi bi-magnet me-2 text-primary" />Lead</NavLink></li>
          <li><NavLink className="dropdown-item" to="/opportunities"><i className="bi bi-briefcase me-2 text-primary" />Opportunity</NavLink></li>
          <li><NavLink className="dropdown-item" to="/accounts"><i className="bi bi-building me-2 text-primary" />Account</NavLink></li>
          <li><NavLink className="dropdown-item" to="/contacts"><i className="bi bi-person-plus me-2 text-primary" />Contact</NavLink></li>
          <li><NavLink className="dropdown-item" to="/quotations"><i className="bi bi-file-earmark-text me-2 text-primary" />Quotation</NavLink></li>
          <li><hr className="dropdown-divider" /></li>
          <li><NavLink className="dropdown-item" to="/activities"><i className="bi bi-telephone me-2 text-secondary" />Log a call</NavLink></li>
          <li><NavLink className="dropdown-item" to="/activities?view=calendar"><i className="bi bi-calendar-plus me-2 text-secondary" />Schedule meeting</NavLink></li>
        </Dropdown>

        {/* Same destination as the sidebar's Help & Guide entry (navModel.js) —
            one help system, reachable two ways, not two. */}
        <NavLink to="/help" className="icon-btn" title="Help" aria-label="Help">
          <i className="bi bi-question-circle" aria-hidden="true" />
        </NavLink>

        <NotificationsMenu />

        <Dropdown
          className="dropdown"
          menuClassName="dropdown-menu-end"
          renderToggle={(props) => (
            <button type="button" className="d-block ms-1 border-0 bg-transparent p-0" aria-label="Account" {...props}>
              <Avatar name={user?.full_name ?? '?'} size={27} className="av-ring" />
            </button>
          )}
        >
          <li className="px-2 py-2 d-flex align-items-center gap-2">
            <Avatar name={user?.full_name ?? '?'} size={32} />
            <div className="min-w-0">
              <div className="t-sm fw-650 text-truncate">{user?.full_name}</div>
              <div className="t-2xs ink-3 text-truncate">{user?.email}</div>
            </div>
          </li>
          <li><hr className="dropdown-divider" /></li>
          <li><NavLink className="dropdown-item" to="/profile"><i className="bi bi-person me-2" />Profile</NavLink></li>
          <li><NavLink className="dropdown-item" to="/profile/sessions"><i className="bi bi-laptop me-2" />Active sessions</NavLink></li>
          <li><hr className="dropdown-divider" /></li>
          <li>
            <button type="button" className="dropdown-item danger w-100 text-start border-0 bg-transparent" onClick={onSignOut}>
              <i className="bi bi-box-arrow-right me-2" />Sign out
            </button>
          </li>
        </Dropdown>
      </div>
    </header>
  );
}

/**
 * The notifications panel.
 *
 * The prototype fills this with five worked examples. They are not reproduced
 * here: §16 of docs/API.md is not built, so anything shown would be invented,
 * and a fake "Follow-up overdue — Meridian Health, 2 days past SLA" is the one
 * kind of placeholder somebody acts on. The panel keeps its shape and says what
 * is missing, and the unread dot stays off until something can set it.
 */
function NotificationsMenu() {
  return (
    <Dropdown
      className="dropdown"
      menuClassName="dropdown-menu-end p-0"
      renderToggle={(props) => (
        <button type="button" className="icon-btn" aria-label="Notifications" {...props}>
          <i className="bi bi-bell" aria-hidden="true" />
        </button>
      )}
    >
      <li>
        <div className="d-flex align-items-center justify-content-between px-3 pt-3 pb-2" style={{ width: 330 }}>
          <div className="fw-650">Notifications</div>
        </div>
      </li>
      <li>
        <div className="px-3 pb-3 pt-1 text-center">
          <div className="notif-ico pill pill-gray mx-auto mb-2"><i className="bi bi-bell-slash" /></div>
          <div className="t-sm fw-600">Nothing here yet</div>
          <div className="t-xs ink-3 mt-1">
            Notifications are specified in §16 of the API and are not built, so this panel has
            nothing real to show.
          </div>
        </div>
      </li>
    </Dropdown>
  );
}

/* -------------------------------------------------------------------------- */
/* Toasts                                                                      */
/* -------------------------------------------------------------------------- */

